import { useEffect, useMemo, useState } from 'react'
import LogsTable from '../components/dashboard/LogsTable'
import { fetchLogs } from '../services/logService'
import { useAuth } from '../context/AuthContext'
import type { AccessDecision, AccessLog } from '../types/access'

/**
 * LogsPage shows the user's audit logs.
 * <p>
 * The backend returns logs, and the frontend filters them by the current user email.
 * We also provide a simple decision filter (ALLOW/CHALLENGE/DENY).
 */
export default function LogsPage() {
  const { userEmail } = useAuth()
  const normalizedUserEmail = userEmail?.trim().toLowerCase() ?? null
  const [logs, setLogs] = useState<AccessLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [decisionFilter, setDecisionFilter] = useState<AccessDecision | 'ALL'>('ALL')

  useEffect(() => {
    if (!normalizedUserEmail) {
      setLogs([])
      setLoading(false)
      setError(null)
      return
    }

    const loadLogs = async () => {
      setLoading(true)
      setError(null)

      try {
        const data = await fetchLogs()
        setLogs(
          data.filter((log) => (log.userEmail ?? '').trim().toLowerCase() === normalizedUserEmail),
        )
      } catch {
        setError('Unable to fetch logs.')
        setLogs([])
      } finally {
        setLoading(false)
      }
    }

    void loadLogs()
  }, [normalizedUserEmail])

  const filteredLogs = useMemo(() => {
    if (decisionFilter === 'ALL') {
      return logs
    }

    // Normalize to uppercase to match our AccessDecision values.
    return logs.filter((log) => log.decision.toUpperCase() === decisionFilter)
  }, [decisionFilter, logs])

  return (
    <section className="glass-card panel">
      <div className="panel-heading">
        <h2>Audit Logs</h2>
        <select
          value={decisionFilter}
          onChange={(event) => setDecisionFilter(event.target.value as AccessDecision | 'ALL')}
        >
          <option value="ALL">All decisions</option>
          <option value="ALLOW">ALLOW</option>
          <option value="CHALLENGE">CHALLENGE</option>
          <option value="DENY">DENY</option>
        </select>
      </div>

      {error && <p className="error-text">{error}</p>}
      <LogsTable logs={filteredLogs} loading={loading} />
    </section>
  )
}

